<script setup lang="ts">
import VuePdfEmbed from 'vue-pdf-embed'
import { isImageFile, isPdfFile } from '@/helpers/utils/files/filePreview'
import { useResizableDialog } from '@/helpers/composables/useResizableDialog'
import DialogResizeHandle from '@/components/helpers/utilities/dialogs/DialogResizeHandle.vue'

interface Props {
  modelValue: boolean
  url?: string | null
  filename?: string
  loading?: boolean
}

const {
  dialogWidth,
  dialogHeight,
  dialogMinWidth,
  dialogMinHeight,
} = useResizableDialog({
  defaultWidth: 720,
  defaultHeight: 520,
  minWidth: 200,
  minHeight: 200,
})

const props = withDefaults(defineProps<Props>(), {
  url: null,
  filename: '',
  loading: false,
})

interface Emit {
  (e: 'update:modelValue', value: boolean): void
}

const emit = defineEmits<Emit>()

const closeDialog = () => {
  emit('update:modelValue', false)
}

const artworkLoading = ref<boolean>(true)

const handleArtworkRendered = () => {
  artworkLoading.value = false
}

watch(
  () => [props.url, props.filename],
  () => {
    artworkLoading.value = isPdfFile(props.filename)
  },
)
</script>

<template>
  <VDialog
    :model-value="modelValue"
    max-width="95vw"
    max-height="95vh"
    :width="dialogWidth"
    :height="dialogHeight"
    class="project-file-preview-resizable-dialog"
    @update:model-value="value => emit('update:modelValue', value)"
  >
    <DialogResizeHandle
      v-model:dialogWidth="dialogWidth"
      v-model:dialogHeight="dialogHeight"
      :min-width="dialogMinWidth"
      :min-height="dialogMinHeight"
    />
    <VCard class="project-file-preview-card d-flex flex-column h-100">
      <VCardTitle class="vss-movable d-flex align-center justify-space-between">
        <span class="text-truncate preview-title">
          {{ filename }}
        </span>
        <VBtn
          icon="mdi-close"
          variant="text"
          size="small"
          @click="closeDialog"
        />
      </VCardTitle>
      <VDivider />
      <VCardText class="project-file-preview-content pa-2">
        <div
          v-if="loading"
          class="preview-loader"
        >
          <VProgressCircular
            indeterminate
            color="primary"
            size="44"
            width="4"
          />
          <div class="text-caption text-medium-emphasis mt-3">
            Loading preview...
          </div>
        </div>
        <div
          v-else-if="url"
          class="preview-container"
        >
          <img
            v-if="isImageFile(filename)"
            :src="url"
            :alt="filename"
            class="preview-image"
          >
          <div
            v-else-if="isPdfFile(filename)"
            class="preview-pdf-wrapper"
          >
            <VOverlay
              :model-value="artworkLoading"
              class="preview-pdf-overlay align-center justify-center"
              contained
              :scrim="false"
            >
              <VProgressCircular
                color="primary"
                indeterminate
              />
            </VOverlay>
            <VuePdfEmbed
              :source="url"
              class="preview-pdf"
              @rendered="handleArtworkRendered"
            />
          </div>

          <iframe
            v-else
            :src="url"
            class="preview-iframe"
            :title="filename"
          />
        </div>
      </VCardText>
    </VCard>
  </VDialog>
</template>

<style scoped lang="scss">
:deep(.project-file-preview-resizable-dialog .v-overlay__content) {
  max-width: 95vw;
  max-height: 95vh;
}

.project-file-preview-card {
  position: relative;
  overflow: hidden;
}

.project-file-preview-content {
  flex: 1 1 auto;
  min-height: 0;
}

.preview-title {
  max-width: 800px;
}

.preview-container {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  min-height: 0;
  overflow: hidden;
}

.preview-loader {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  min-height: 0;
}

.preview-pdf-overlay {
  pointer-events: none;
}

.preview-pdf-wrapper {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: auto;
}

.preview-image {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  border-radius: 4px;
}

.preview-pdf {
  width: 100%;
  height: 100%;
  max-height: 100%;
  overflow-y: auto;
}

.preview-iframe {
  width: 100%;
  height: 100%;
  border: none;
}
</style>
